import {
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  Button,
  DateRangePicker,
  H2,
  Modal,
  MultiSelect,
  NumberInput,
  Pagination,
  Table,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  GOOD_AMOUNT_HISTORY,
  GOOD_AMOUNT_HISTORY_CSV,
  IGoodHistoryItem,
  IGoodsAmountHistoryRequest,
  IGoodsAmountHistoryResponse,
} from 'schemas';
import notification from 'services/notificationService';
import { EGoodsAmountOperating } from 'types';
import { formatDateForServer } from 'utils';

import s from './AmountReportModal.module.scss';
import { IAmountReportModalProps, IFormValues } from './interfaces';
import { useColumns } from './useColumns';

export const AmountReportModal: FC<IAmountReportModalProps> = ({
  good,
  onClose,
}) => {
  const { t, lang } = useContext(LocalizationContext);

  const dateRef = useRef<HTMLDivElement | null>(null);
  const size = useWindowResize();

  const [selectedOperating, setSelectedOperating] = useState<
    EGoodsAmountOperating[] | null
  >(null);
  const [goodAmountHistory, setGoodAmountHistory] = useState<
    IGoodHistoryItem[] | null
  >(null);
  const [pagesInfo, setPagesInfo] = useState({ currentPage: 1, lastPage: 1 });

  const [getGoodsAmountHistory, { loading: getGoodsAmountHistoryLoading }] =
    useLazyQuery<IGoodsAmountHistoryResponse, IGoodsAmountHistoryRequest>(
      GOOD_AMOUNT_HISTORY,
      {
        onCompleted: (res) => {
          setGoodAmountHistory(res?.goodHistory?.data);
          setPagesInfo({ ...res?.goodHistory?.paginatorInfo });
        },
        onError: (error) => {
          notification.info({
            t,
            type: 'error',
            title: t('errors.error'),
            message: error?.message,
          });
        },
        fetchPolicy: 'cache-and-network',
      }
    );

  const [getGoodHistoryCsv, { loading: getGoodHistoryCsvLoading }] =
    useLazyQuery(GOOD_AMOUNT_HISTORY_CSV, {
      onError: (error) => {
        notification.info({
          t,
          type: 'error',
          title: t('errors.error'),
          message: error?.message,
        });
      },
      fetchPolicy: 'cache-and-network',
    });

  const today = dayjs();
  const { lastPage, currentPage } = pagesInfo;
  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();

  useLayoutEffect(() => {
    if (!dateRef.current) return;
    if (size.width < 1864) return;
    const updateWidth = (): void => {
      const datePickerWidth = dateRef.current?.offsetWidth || 0;
      document.documentElement.style.setProperty(
        '--datepicker-width',
        `${datePickerWidth}px`
      );
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(dateRef.current);

    return () => observer.disconnect();
  }, [startDate, endDate, periodType, offset, lang]);

  const OPTIONS = [
    { title: t('warehouse.operation.add'), value: EGoodsAmountOperating.ADD },
    { title: t('log.payment.refund'), value: EGoodsAmountOperating.REFUND },
    {
      title: t('goods.amount.report.table.create'),
      value: EGoodsAmountOperating.CREATE,
    },
    {
      title: t('warehouse.operation.disposal'),
      value: EGoodsAmountOperating.DISPOSAL,
    },
    { title: t('log.payment.created'), value: EGoodsAmountOperating.SOLD },
  ];

  const columns = useColumns();

  const handlePageChange = (page: number): void => {
    if (page !== currentPage) {
      setPagesInfo((prev) => ({ ...prev, currentPage: page }));

      getGoodsAmountHistory({
        variables: {
          input: {
            page: page,
            id: good?.id,
            from: formatDateForServer(dayjs(startDate)),
            to: formatDateForServer(dayjs(endDate)),
            operations: selectedOperating,
          },
        },
      });
    }
  };

  const { control, handleSubmit, setValue } = useForm<IFormValues>({});

  useEffect(() => {
    setValue('page', pagesInfo.currentPage);
  }, [pagesInfo.currentPage, setValue]);

  useEffect(() => {
    getGoodsAmountHistory({
      variables: {
        input: {
          page: 1,
          id: good?.id,
          from: formatDateForServer(dayjs(startDate)),
          to: formatDateForServer(dayjs(endDate)),
          operations: selectedOperating,
        },
      },
    });
  }, []);

  return (
    <Modal className={s.container} onClose={onClose}>
      {good && <H2>{t('goods.amount.report.title', good?.title)}</H2>}

      <div className={s.controls}>
        <div className={s.left}>
          <div ref={dateRef} className={s.date_block}>
            <DateRangePicker
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              lastValidDate={today.format()}
              firstValidDate={dayjs('2022-03-01T00:00:00').format()}
              lang={lang}
              periodType={periodType}
              setPeriodType={setPeriodType}
              offset={offset}
              setOffset={setOffset}
            />
          </div>
          <div className={s.multi}>
            <MultiSelect
              name="operating-name"
              title={t('transfers.all')}
              variant="secondary"
              options={OPTIONS}
              value={selectedOperating}
              onChange={(value) => setSelectedOperating(value)}
              className={s.select}
            />
          </div>
        </div>
        <div className={s.right}>
          <Button
            onClick={() => {
              return getGoodsAmountHistory({
                variables: {
                  input: {
                    id: good?.id,
                    from: formatDateForServer(dayjs(startDate)),
                    to: formatDateForServer(dayjs(endDate)),
                    operations: selectedOperating,
                  },
                },
              });
            }}
            disabled={getGoodsAmountHistoryLoading}
            className={s.generate}
          >
            {t('reports.generate')}
          </Button>
          <Button
            className={s.export_mobile}
            iconName="IncomeIcon"
            variant="outline"
            isLoading={getGoodHistoryCsvLoading}
            onClick={() => {
              return getGoodHistoryCsv({
                variables: {
                  input: {
                    id: good?.id,
                    from: formatDateForServer(dayjs(startDate)),
                    to: formatDateForServer(dayjs(endDate)),
                    operations: selectedOperating,
                  },
                },
              }).then((res) => {
                const url = res?.data?.goodHistory?.url;
                if (url) {
                  open(url, '_self');
                }
              });
            }}
            disabled={
              getGoodsAmountHistoryLoading || goodAmountHistory?.length === 0
            }
          >
            {t('export.csv')}
          </Button>
        </div>
      </div>

      <Table
        data={goodAmountHistory ?? []}
        columns={columns}
        className={cn(s.table, !goodAmountHistory?.length && s.empty)}
        isLoading={getGoodsAmountHistoryLoading}
        emptyDataPlaceholder={t('report.placeholder')}
      />
      {lastPage > 1 && (
        <div className={s.pagination_bottom}>
          <Pagination
            onChange={(page: number) => {
              if (page != pagesInfo.currentPage) {
                handlePageChange(page);
              }
            }}
            lastPage={lastPage}
            currentPage={currentPage}
            className={s.pagination}
            isChose={true}
          />
          {pagesInfo && (
            <form
              onSubmit={handleSubmit(({ page }) => handlePageChange(page || 1))}
            >
              <Controller
                name="page"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <NumberInput
                    value={value}
                    onChange={(val) => {
                      if (typeof val === 'number') {
                        if (val < 1) {
                          onChange(1);
                        } else if (val > pagesInfo?.lastPage) {
                          onChange(pagesInfo?.lastPage);
                        } else {
                          onChange(val);
                        }
                      } else {
                        onChange('');
                      }
                    }}
                    variant="secondary"
                    placeholder="№"
                    className={s.pagination_input}
                  />
                )}
              />
              <button type="submit" style={{ display: 'none' }} />
            </form>
          )}
        </div>
      )}
    </Modal>
  );
};
